import { MapPin } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useResolveGeo } from '@/hooks/use-resolve-geo';

import type { WizardForm } from './step-connection';

interface StepLocationProps {
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
}

export function StepLocation({ form, onChange }: StepLocationProps): JSX.Element {
  const geo = useResolveGeo(form.public_ip);
  const prefilledIp = useRef('');

  useEffect(() => {
    if (!geo.data || prefilledIp.current === form.public_ip) return;
    prefilledIp.current = form.public_ip;
    onChange({
      region_continent: form.region_continent || (geo.data.continent ?? ''),
      region_country: form.region_country || (geo.data.country ?? ''),
      region_city: form.region_city || (geo.data.city ?? ''),
    });
  }, [geo.data, form.public_ip, form.region_continent, form.region_country, form.region_city, onChange]);

  const resolvedLabel = geo.data
    ? [geo.data.city, geo.data.country, geo.data.continent].filter(Boolean).join(' · ')
    : null;

  return (
    <div className="space-y-4">
      {/* Geo resolution card */}
      <div className="rounded-md border p-3 flex items-start gap-3 min-h-[3.5rem]">
        <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="text-sm">
          {geo.isFetching && (
            <span className="text-muted-foreground">Resolving location from {form.public_ip}…</span>
          )}
          {!geo.isFetching && geo.isError && (
            <span className="text-amber-600">Could not resolve location — enter it manually.</span>
          )}
          {!geo.isFetching && geo.data && (
            <>
              <span className="font-medium">Resolved from IP: </span>
              <span className="text-muted-foreground">{resolvedLabel}</span>
            </>
          )}
          {!geo.isFetching && !geo.isError && !geo.data && (
            <span className="text-muted-foreground">Enter location manually.</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label htmlFor="w-continent">Continent</Label>
          <Input
            id="w-continent"
            value={form.region_continent}
            onChange={(e) => onChange({ region_continent: e.target.value })}
            placeholder="Europe"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="w-country">Country</Label>
          <Input
            id="w-country"
            value={form.region_country}
            onChange={(e) => onChange({ region_country: e.target.value })}
            placeholder="DE"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="w-city">City</Label>
          <Input
            id="w-city"
            value={form.region_city}
            onChange={(e) => onChange({ region_city: e.target.value })}
            placeholder="Falkenstein"
          />
        </div>
      </div>
    </div>
  );
}
